/**
 * Admin — Benefits & Coupons console.
 *
 * The single place to manage every pricing campaign: create, edit, pause,
 * archive and delete them, watch how they perform, and read the audit trail.
 *
 * Scope note: this screen is administration only. It writes to the coupon
 * tables and the audit log. Authentication, checkout, the Pricing Engine, the
 * payment gateways, webhooks and memberships are untouched — the engine keeps
 * reading exactly the columns it always has.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, BarChart3, Gift, History, Lock, ShieldCheck, Ticket } from "lucide-react";
import CouponsManager from "@/components/admin/benefits/CouponsManager";
import CouponAnalytics from "@/components/admin/benefits/CouponAnalytics";
import CouponAuditLog from "@/components/admin/benefits/CouponAuditLog";
import { useAdminRole } from "@/hooks/useAdminRole";
import { PRICING_VERSION } from "@/pricing";
import {
  REGION_OPTIONS,
  auditIpHash,
  canonicalMrp,
  formatMoney,
  type CouponRow,
} from "@/lib/couponConsole";

const AdminBenefitsConsole = () => {
  const { isSuperAdmin } = useAdminRole();
  const [coupons, setCoupons] = useState<CouponRow[]>([]);

  // Canonical price change — a super admin request, recorded for governance.
  const [region, setRegion] = useState("IN");
  const [newPrice, setNewPrice] = useState("");
  const [reason, setReason] = useState("");
  const [requesting, setRequesting] = useState(false);

  const current = canonicalMrp(region);

  const requestMrpChange = async () => {
    setRequesting(true);
    const { error } = await supabase.rpc("request_canonical_mrp_change", {
      p_region: region,
      p_currency: current.currency,
      p_old_value: current.amount,
      p_new_value: Number(newPrice),
      p_reason: reason || null,
      p_ip_hash: await auditIpHash(),
    });
    setRequesting(false);
    if (error) {
      toast.error("That request was not recorded", { description: error.message });
      return;
    }
    toast.success("Price change requested", {
      description: "Recorded for governance. The live MRP changes when the request is released.",
    });
    setNewPrice("");
    setReason("");
  };

  const activeCount = coupons.filter((c) => c.status === "active" && !c.deleted_at).length;

  return (
    <div className="min-h-screen bg-background">
      <PageHead
        title="Benefits & Coupons — Admin"
        description="Create and manage every pricing campaign, watch performance and read the full audit trail."
        path="/admin/benefits"
      />

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <header className="space-y-4">
          <Button variant="ghost" size="sm" asChild className="min-h-11 -ml-2">
            <Link to="/admin">
              <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
              Admin Hub
            </Link>
          </Button>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Benefits & Coupons</h1>
              <p className="text-muted-foreground mt-2 max-w-2xl">
                Every reason someone pays less, in one place. Create a campaign, see what it earns,
                and pause it the moment you need to — no developer required.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{activeCount} active</Badge>
              <Badge variant="outline">Pricing v{PRICING_VERSION}</Badge>
              {isSuperAdmin && (
                <Badge className="bg-primary/15 text-primary">
                  <ShieldCheck className="w-3 h-3 mr-1" aria-hidden="true" />
                  Super admin
                </Badge>
              )}
            </div>
          </div>
        </header>

        <Tabs defaultValue="campaigns">
          <TabsList className="w-full grid grid-cols-2 sm:grid-cols-4 h-auto">
            <TabsTrigger value="campaigns" className="min-h-11">
              <Ticket className="w-4 h-4 mr-2" aria-hidden="true" />
              Campaigns
            </TabsTrigger>
            <TabsTrigger value="analytics" className="min-h-11">
              <BarChart3 className="w-4 h-4 mr-2" aria-hidden="true" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="audit" className="min-h-11">
              <History className="w-4 h-4 mr-2" aria-hidden="true" />
              Audit log
            </TabsTrigger>
            <TabsTrigger value="governance" className="min-h-11">
              <Lock className="w-4 h-4 mr-2" aria-hidden="true" />
              Pricing
            </TabsTrigger>
          </TabsList>

          <TabsContent value="campaigns" className="pt-6">
            <CouponsManager isSuperAdmin={isSuperAdmin} onCouponsLoaded={setCoupons} />
          </TabsContent>

          <TabsContent value="analytics" className="pt-6">
            <CouponAnalytics coupons={coupons} />
          </TabsContent>

          <TabsContent value="audit" className="pt-6">
            <CouponAuditLog />
          </TabsContent>

          <TabsContent value="governance" className="pt-6 space-y-6">
            <Card className="rounded-3xl">
              <CardHeader>
                <CardTitle className="text-base">Canonical price</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <p className="text-sm text-muted-foreground">
                  The MRP is the one number no campaign can change. Every discount is calculated
                  from it, so a change here moves every price in that region.
                </p>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="g-region">Region</Label>
                    <Select value={region} onValueChange={setRegion}>
                      <SelectTrigger id="g-region" className="min-h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REGION_OPTIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label} {r.live ? "" : "(not live)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Current MRP</Label>
                    <p className="text-2xl font-bold pt-1">
                      {formatMoney(current.amount, current.currency)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="g-price">Requested MRP</Label>
                    <Input
                      id="g-price"
                      type="number"
                      inputMode="decimal"
                      min={1}
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      disabled={!isSuperAdmin}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="g-reason">Why</Label>
                  <Input
                    id="g-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Recorded in the audit log"
                    disabled={!isSuperAdmin}
                  />
                </div>

                {!isSuperAdmin ? (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Lock className="w-4 h-4" aria-hidden="true" />
                    Only a super admin can request a change to the canonical price.
                  </p>
                ) : (
                  <Button
                    className="min-h-12"
                    disabled={requesting || !newPrice}
                    onClick={() => void requestMrpChange()}
                  >
                    Request price change
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-3xl">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Gift className="w-4 h-4 text-primary" aria-hidden="true" />
                  Scholarships, partners and invitations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Account-based benefits — scholarship approvals, partner and institution pricing,
                  and private invitation codes — are operated on the Access &amp; Benefits screen.
                </p>
                <Button variant="outline" className="min-h-12" asChild>
                  <Link to="/admin/access-benefits">Open Access &amp; Benefits</Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminBenefitsConsole;
